'use client'

import { Button } from '@heroui/button'
import { Textarea } from '@heroui/input'
import { useDisclosure } from '@heroui/modal'
import { ScrollShadow } from '@heroui/scroll-shadow'
import {
  DateValue,
  getLocalTimeZone,
  today,
  parseDate as parseInternationalDate,
} from '@internationalized/date'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { isFuture } from 'date-fns'
import { useRouter } from 'next/navigation'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  BlurHeader,
  CrossIcon,
  Image3Icon,
  InsightsIcon,
  LetterSpacingIcon,
  Tag3Icon,
  Timer2Icon,
} from '@/components/shared'
import {
  AddTagModal,
  CreateMomentDateInput,
  CreateMomentStatusModal,
  GenerateTranscriptModal,
  ImageList,
  CancelModal,
  CreateLetterModal,
} from '@/components/widgets'
import {
  CONTENT_DRAFT_STORAGE_KEY,
  TAGS_DRAFT_STORAGE_KEY,
  TITLE_DRAFT_STORAGE_KEY,
} from '@/constants'
import { useCreateMoments, useUpdateMoment } from '@/data'
import { useDecrypt } from '@/hooks'
import { authClient } from '@/lib/auth-client'
import { routes } from '@/routes'
import { useEncryptionKey } from '@/store'
import { useApi } from '@/trpc/react'
import { cn, decrypt, isTouchDevice } from '@/utils'



const MAX_IMAGES = 8
const MAX_BYTES_LIMIT = 30 * 1024 * 1024 // 30MB

export interface FormMomentProps {
  mode?: 'create' | 'edit'
  initialData?: {
    id?: string
    title: string
    content: string
    images: {
      original: { path: string; type: string }[]
      thumbnail: { path: string; type: string }[]
    }
    tags: string[]
    date: string
  }
}

const parseDate = (dateString: string): DateValue => {
  try {
    const date = new Date(dateString)
    const formattedDate = date.toISOString().split('T')[0]
    return parseInternationalDate(formattedDate)
  } catch (error) {
    console.error('Error parsing date:', error)
    return today(getLocalTimeZone())
  }
}

const parseTags = (tags: string) => {
  let tagsArray: string[] = []
  try {
    if (tags) {
      const parsed = JSON.parse(tags)
      if (
        Array.isArray(parsed) &&
        parsed.every((item) => typeof item === 'string')
      ) {
        tagsArray = parsed
      }
    }
  } catch (error) {
    return []
  }
  return tagsArray
}

const formatTimeSpent = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = Math.floor(totalSeconds % 60)
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

const FormMoment = ({ mode = 'create', initialData }: FormMomentProps) => {
  const api = useApi()
  const { data: session } = authClient.useSession()
  const encryptionKey = useEncryptionKey()
  const userId = session?.user.id ?? ''
  const [startTime] = useState<number>(Date.now())
  const [spentTimeInMinutes, setSpentTimeInMinutes] = useState<string>('00:00')
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLTextAreaElement>(null)
  const titleRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [newImages, setNewImages] = useState<string[]>([])
  const [newFiles, setNewFiles] = useState<File[]>([])
  const [createdMomentId, setCreatedMomentId] = useState<string | undefined>(
    undefined,
  )
  const { data: userSettings } = useQuery(api.userSettings.get.queryOptions())
  const {
    isOpen: isOpenAddTagModal,
    onOpenChange: onOpenChangeAddTagModal,
    onOpen: onOpenAddTagModal,
  } = useDisclosure()
  const {
    isOpen: isOpenFutureDateModal,
    onOpenChange: onOpenChangeFutureDateModal,
    onOpen: onOpenFutureDateModal,
  } = useDisclosure()
  const {
    isOpen: isOpenCancelModal,
    onOpenChange: onOpenChangeCancelModal,
    onOpen: onOpenCancelModal,
  } = useDisclosure()
  const {
    isOpen: isOpenGenerateTranscriptModal,
    onOpenChange: onOpenChangeGenerateTranscriptModal,
    onOpen: onOpenGenerateTranscriptModal,
  } = useDisclosure()
  const {
    isOpen: isOpenCreateMomentStatusModal,
    onOpenChange: onOpenChangeCreateMomentStatusModal,
    onOpen: onOpenCreateMomentStatusModal,
  } = useDisclosure()
  const {
    mutate: createMoment,
    isPending: isCreating,
    status: createStatus,
  } = useCreateMoments({
    onSuccess: (momentId) => {
      setCreatedMomentId(momentId)
    },
  })
  const {
    mutate: updateMoment,
    isPending: isUpdating,
    status: updateStatus,
  } = useUpdateMoment({
    onSuccess: (momentId) => {
      setCreatedMomentId(momentId)
    },
  })

  const originalImages = initialData?.images?.original ?? []
  const thumbnailImages = initialData?.images?.thumbnail ?? []
  const { data: userContext } = useDecrypt({
    ciphertext: userSettings?.generalInformation ?? '',
  })
  const [date, setDate] = useState<DateValue>(
    mode === 'edit' && initialData?.date
      ? parseDate(initialData.date)
      : today(getLocalTimeZone()),
  )
  const [title, setTitle] = useState<string>('')
  const [content, setContent] = useState<string>('')
  const [files, setFiles] = useState<File[]>([])
  const [tags, setTags] = useState<string[]>(
    mode === 'edit' ? (initialData?.tags ?? []) : [],
  )
  const router = useRouter()

  const handleInitialData = useCallback(async () => {
    if (!initialData || !encryptionKey) return
    const decryptedContent = await decrypt(
      initialData?.content ?? '',
      encryptionKey ?? '',
    )
    const decryptedTitle = await decrypt(
      initialData?.title ?? '',
      encryptionKey ?? '',
    )

    setContent((prev: string) => {
      if (prev) return prev
      if (mode === 'create') {
        return localStorage.getItem(CONTENT_DRAFT_STORAGE_KEY) ?? ''
      }
      return decryptedContent ?? ''
    })
    setTitle((prev: string) => {
      if (prev) return prev
      if (mode === 'create') {
        return localStorage.getItem(TITLE_DRAFT_STORAGE_KEY) ?? ''
      }
      return decryptedTitle ?? ''
    })
    setTags(parseTags(localStorage.getItem(TAGS_DRAFT_STORAGE_KEY) ?? ''))
  }, [mode, initialData, encryptionKey])

  useEffect(() => {
    handleInitialData()
  }, [handleInitialData])

  useEffect(() => {
    if (mode === 'edit' && titleRef.current) {
      const length = titleRef.current.value.length
      titleRef.current.setSelectionRange(length, length)
      titleRef.current.focus()
    }
  }, [mode])

  const handleImageInput = () => {
    // trigger the file input dialog
    fileInputRef.current?.click()
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const inputFiles = event.target.files
    if (!inputFiles || inputFiles.length === 0) return
    const filesArray = Array.from(inputFiles)
    const availableLimit =
      MAX_IMAGES - (originalImages.length + newImages.length)
    const currentMbSize = files.reduce((acc, file) => acc + file.size, 0)
    if (availableLimit <= 0 || currentMbSize >= MAX_BYTES_LIMIT) return
    // modify the files array to limit the number of files to MAX_IMAGES
    const previewableFiles = filesArray
      .slice(0, availableLimit)
      .filter((file) => {
        // Check if the file can be previewed (e.g., images)
        return file.type.startsWith('image/')
      })

    if (!previewableFiles.length) return

    try {
      const urls = await Promise.all(
        previewableFiles.map(async (file) => {
          const url = URL.createObjectURL(file)
          const img = new Image()
          img.src = url

          await new Promise((resolve) => {
            img.onload = () => resolve(true)
            img.onerror = () => {
              URL.revokeObjectURL(url)
              resolve(false)
            }
          })

          return img.complete ? url : ''
        }),
      )

      const validUrls = urls.filter(Boolean)
      const validFiles = previewableFiles.filter((_, i) => urls[i])

      if (mode === 'edit') {
        setNewFiles((prev) => [...prev, ...validFiles])
      } else {
        setFiles((prev) => [...prev, ...validFiles])
      }
      setNewImages((prev) => [...prev, ...validUrls])
    } catch (error) {
      console.error('Error loading images:', error)
    }

    event.target.value = ''
  }

  const handleDateChange = (value: DateValue) => {
    const localDate = value.toDate(getLocalTimeZone())
    if (isFuture(localDate)) {
      onOpenFutureDateModal()
    }
    setDate(value)
  }

  const handleSaveTag = (tag: string) => {
    setTags((prev) => {
      const newTags = [...prev, tag]
      localStorage.setItem(TAGS_DRAFT_STORAGE_KEY, JSON.stringify(newTags))
      return newTags
    })
  }

  const handleDeleteTag = (tag: string) => {
    setTags((prev) => {
      const newTags = prev.filter((t) => t !== tag)
      localStorage.setItem(TAGS_DRAFT_STORAGE_KEY, JSON.stringify(newTags))
      return newTags
    })
  }

  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    const input = e.target
    if (containerRef.current && isTouchDevice()) {
      // containerRef.current.style.height = 'calc(100dvh - 280px)'
      input.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleOnBlur = () => {
    if (containerRef.current) {
      // containerRef.current.style.height = '100dvh'
    }
  }

  const handleContentChange = (value: string) => {
    setContent(value)
    localStorage.setItem(CONTENT_DRAFT_STORAGE_KEY, value)
  }

  const onSuccessTranscript = (transcript: string) => {
    setContent((prev) => {
      const newContent = `${prev}\n\n${transcript}`
      localStorage.setItem(CONTENT_DRAFT_STORAGE_KEY, newContent)
      return newContent
    })
  }

  const handleTitleChange = (value: string) => {
    setTitle(value)
    localStorage.setItem(TITLE_DRAFT_STORAGE_KEY, value)
  }

  const handleSubmit = async () => {
    const totalSpentSeconds = (Date.now() - startTime) / 1000
    const formattedSpentTime = formatTimeSpent(totalSpentSeconds)
    setSpentTimeInMinutes(formattedSpentTime)

    onOpenCreateMomentStatusModal()

    try {
      if (mode === 'edit' && initialData?.id) {
        updateMoment({
          momentId: initialData.id,
          title,
          description: content,
          tags,
          date: date.toString(),
          userId,
          userContext: userContext ?? '',
          userSettingsId: userSettings?.id ?? '',
          zone: getLocalTimeZone(),
          newImages: newFiles,
        })
      } else {
        createMoment({
          title,
          description: content,
          images: files,
          tags,
          date: date.toString(),
          userContext: userContext ?? '',
          zone: getLocalTimeZone(),
        })
      }
    } catch (error) {
      console.error('Error processing moment:', error)
    }
  }

  const handleClose = () => {
    if (mode === 'edit') {
      resetForm()
      resetDrafts()
      router.back()
      return
    }

    if (title || content || newImages.length) {
      return onOpenCancelModal()
    }

    resetForm()
    resetDrafts()
    router.back()
  }

  const handleCloseCancelModal = () => {
    resetDrafts()
    router.back()
  }

  const handleRemoveImage = (index: number) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index))
    if (mode === 'edit') {
      setNewFiles((prev) => prev.filter((_, i) => i !== index))
    } else {
      setFiles((prev) => prev.filter((_, i) => i !== index))
    }
  }

  const resetDrafts = () => {
    localStorage.setItem(CONTENT_DRAFT_STORAGE_KEY, '')
    localStorage.setItem(TITLE_DRAFT_STORAGE_KEY, '')
    localStorage.setItem(TAGS_DRAFT_STORAGE_KEY, JSON.stringify([]))
  }

  const resetForm = () => {
    setContent('')
    setTitle('')
    setNewImages([])
    setNewFiles([])
    if (mode === 'create') {
      setFiles([])
    }
  }

  const isDisabled = content.trim().length === 0
  const availableLimit = MAX_IMAGES - (originalImages.length + newImages.length)
  const currentMbSize = files.reduce((acc, file) => acc + file.size, 0)

  const isFileInputDisabled =
    availableLimit <= 0 || currentMbSize >= MAX_BYTES_LIMIT

  return (
    <>
      <div
        ref={containerRef}
        className="relative flex min-h-[100dvh] w-full flex-1 flex-col items-center gap-4 pt-20"
      >
        <div className="fixed left-0 right-0 top-0 z-50 grid w-full grid-cols-3 items-center px-4 pt-3">
          <BlurHeader className="h-[80px]" />
          <Button
            isIconOnly
            size="md"
            variant="solid"
            color="default"
            className="rounded-full bg-default-100"
            onPress={handleClose}
          >
            <CrossIcon className="text-2xl" />
          </Button>
          <div className="z-30 flex justify-center gap-3 text-2xl text-default-500">
            <Image3Icon
              className={cn(
                'cursor-pointer',
                isFileInputDisabled && 'cursor-not-allowed opacity-50',
              )}
              onClick={handleImageInput}
            />
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="capture=camera,image/*"
              onChange={handleFileChange}
              multiple
              disabled={isFileInputDisabled}
            />
            <InsightsIcon
              className="cursor-pointer"
              onClick={onOpenGenerateTranscriptModal}
            />
            {mode === 'create' && (
              <Tag3Icon
                className="cursor-pointer"
                onClick={onOpenAddTagModal}
              />
            )}
          </div>
          <Button
            isDisabled={isDisabled}
            isLoading={mode === 'edit' ? isUpdating : isCreating}
            variant="solid"
            size="sm"
            color="primary"
            onPress={handleSubmit}
            className="primary-button justify-self-end"
          >
            {mode === 'edit' ? 'Update' : 'Create'}
          </Button>
        </div>
        <div className="flex h-full w-full max-w-[640px] flex-1 flex-col items-center gap-2">
          {(originalImages.length > 0 || newImages.length > 0) && (
            <ScrollShadow
              className="flex h-[60px] w-full items-end px-4"
              orientation="horizontal"
              hideScrollBar
              visibility="none"
            >
              <div className="flex flex-wrap gap-2">
                <ImageList
                  withPreview
                  encryptedImages={{
                    original: originalImages,
                    thumbnail: thumbnailImages,
                  }}
                />
                {newImages.length > 0 && (
                  <ImageList
                    withPreview
                    images={newImages}
                    onRemove={handleRemoveImage}
                  />
                )}
              </div>
            </ScrollShadow>
          )}
          <div className="flex w-full self-start px-4">
            <CreateMomentDateInput
              value={date}
              onChange={handleDateChange}
              mode={mode}
              initialDate={initialData?.date}
            />
          </div>
          <div className="flex w-full flex-col gap-2 px-4">
            <Textarea
              autoComplete="off"
              minRows={1}
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              variant="bordered"
              fullWidth
              ref={titleRef}
              value={title}
              placeholder="Introduce Headline"
              classNames={{
                base: 'w-full max-w-[640px]',
                input: `font-crimsonPro text-4xl font-medium leading-10 placeholder:text-default-500 tracking-[-0.36px]`,
                inputWrapper: cn(
                  'p-0 shadow-none bg-background focus:bg-background hover:bg-background border-none',
                ),
              }}
              onChange={(e) => handleTitleChange(e.target.value)}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
            />
            <Textarea
              value={content}
              ref={contentRef}
              minRows={10}
              placeholder="Write here..."
              onChange={(e) => handleContentChange(e.target.value)}
              radius="none"
              disableAutosize={false}
              fullWidth
              variant="bordered"
              maxRows={Infinity}
              data-hover="true"
              classNames={{
                base: 'w-full max-w-[640px] h-full flex-1',
                input: `font-crimsonPro text-lg leading-[22px] font-light placeholder:text-default-500 tracking-[-0.18px] pb-10`,
                innerWrapper: 'h-full flex-1',
                inputWrapper: cn(
                  'p-0 shadow-none bg-background focus:bg-background hover:bg-background border-none h-full flex-1 items-start',
                ),
              }}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
            />
          </div>
        </div>
      </div>
      <AddTagModal
        isOpen={isOpenAddTagModal}
        onOpenChange={onOpenChangeAddTagModal}
        editable
        onSaveTag={handleSaveTag}
        onDeleteTag={handleDeleteTag}
        tags={tags}
      />
      <CancelModal
        title="Leave without saving?"
        description={
          'This action cannot be undone. Your current moment will be lost if you don’t save it.'
        }
        isOpen={isOpenCancelModal}
        onOpenChange={onOpenChangeCancelModal}
        cancelButtonTitle="Cancel"
        action={{
          title: 'Discard',
          onClick: handleCloseCancelModal,
        }}
      />
      <GenerateTranscriptModal
        isOpen={isOpenGenerateTranscriptModal}
        onOpenChange={onOpenChangeGenerateTranscriptModal}
        onSuccess={onSuccessTranscript}
      />
      <CreateLetterModal
        isOpen={isOpenFutureDateModal}
        onOpenChange={onOpenChangeFutureDateModal}
        date={date.toDate(getLocalTimeZone())}
      />
      <CreateMomentStatusModal
        isOpen={isOpenCreateMomentStatusModal}
        onOpenChange={onOpenChangeCreateMomentStatusModal}
        onSuccess={async () => {
          resetDrafts()
          if (mode === 'edit') {
            router.back()
          } else {
            router.push(routes.dashboard)
          }
        }}
        date={date.toDate(getLocalTimeZone())}
        momentId={createdMomentId}
        status={mode === 'edit' ? updateStatus : createStatus}
        statistics={[
          {
            label: 'Words',
            value: content.split(' ').length.toString(),
            icon: <LetterSpacingIcon className="text-xl" />,
          },
          {
            label: 'Time',
            value: spentTimeInMinutes.toString(),
            icon: <Timer2Icon className="text-xl" />,
          },
        ]}
      />
    </>
  )
}

export default React.memo(FormMoment, (prevProps, nextProps) => {
  return (
    prevProps.mode === nextProps.mode &&
    prevProps.initialData?.id === nextProps.initialData?.id
  )
})
